import React, { useState, useEffect, useContext } from 'react';

import { useHistory, useLocation } from 'react-router-dom'

import { SettingsContext, ModalContext } from '../contexts/index.jsx'
import { ProductSearchHelper } from "../helpers/index.js"
import { Button, Icon, SortSelect } from "../components/ui"

import {
    SearchFilters, FilterBullets
} from './Search/index.jsx'
import RCFilters from './Search/RCFilters'
import './Listing/rc-product-view.scss'

const useRcSearchToolbar = (pathname) =>
    /^\/product\/\d+$/.test(pathname) || pathname.startsWith('/products')

const Search = ({ withFilters = false }) => {
    const [ filtersShown, setFiltersShown ] = useState(false)
    const { setSearchShown } = useContext(ModalContext)
    const settings = useContext(SettingsContext)

    useEffect(() => {
        setSearchShown(filtersShown)
    }, [ filtersShown ])

    const history = useHistory()
    const location = useLocation()

    const search = ProductSearchHelper.parseUrl(location, false)
    const [ inputText, setInputText ] = useState(search.query)

    const onSearchChange = () => {
        const url = ProductSearchHelper.buildUrl(search)
        history.push(url + `?${settings.iframeQueryParams}`)
    }

    const onInputChange = (e) => {
        e.preventDefault()
        setInputText(e.target.value)
    }

    const onSubmit = (e) => {
        e.preventDefault()

        setFiltersShown(false)
        search.query = inputText

        onSearchChange()
    }

    const onSortChange = (field, direction) => {
        search.sort = { field, direction }

        onSearchChange()
    }

    const onFilterByClick = (e) => {
        e.preventDefault();
        setFiltersShown(!filtersShown)
    }

    const onFiltersApplied = (search) => {
        const url = ProductSearchHelper.buildUrl(search)
        setFiltersShown(false)
        history.push(url + `?${settings.iframeQueryParams}`)
    }

    const onBulletRemoved = ({ filter, id }) => {
        search.filters[filter] = search.filters[filter].filter((o) => o.id !== id)
        onSearchChange()
    }

    const getSortOptions = (categoryId) => {
        const options = [
            { field: 'created_at', direction: 'desc', label: 'Most recent' },
            { field: 'price', direction: 'asc', label: 'Price: low to high' },
            { field: 'price', direction: 'desc', label: 'Price: high to low' }
        ]

        if (categoryId === 9) {
            options.push({ field: 'is_investment', direction: 'desc', label: 'Best investment' })
        }

        return options
    }

    const sortOptions = getSortOptions(search.filters.category_id)
    const rcToolbar = useRcSearchToolbar(location.pathname)

    return (
        <div
            id="search"
            className={`mt-0${rcToolbar ? ' rc-product-search-toolbar' : ' mt-xl-5'}`}
        >
            <div className="d-flex justify-content-between content">
                {withFilters && (
                    <div className="align-self-center filter-container d-none d-xl-inline-block">
                        <Button
                            variant="secondary"
                            size="lg"
                            leadingIcon={<Icon name="SlidersHorizontal" size="sm" />}
                            onClick={onFilterByClick}
                            data-btn="product-filter"
                        >
                            Filter by
                        </Button>
                    </div>
                )}

                <form onSubmit={onSubmit} className="flex-grow-1">
                    <div className={`d-flex${rcToolbar ? ' rc-product-search-pill' : ' justify-content-center'}`}>
                        {rcToolbar && (
                            <div className="rc-product-search-pill__icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                    <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
                                    <path d="M14 14L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            </div>
                        )}
                        <input
                            type="text"
                            className="search"
                            onChange={onInputChange}
                            value={inputText}
                            placeholder={rcToolbar ? 'Search products…' : 'Search'}
                        />
                    </div>

                    <FilterBullets filters={search.filters} onRemove={onBulletRemoved} />
                </form>

                <div className="d-xl-none align-self-start">
                    <Button
                        variant="secondary"
                        size="lg"
                        leadingIcon={<Icon name="SlidersHorizontal" size="sm" />}
                        onClick={onFilterByClick}
                        data-btn="product-filter-mobile"
                    >
                        Filter
                    </Button>
                </div>

                {withFilters && (
                    <div className={`align-self-center ${rcToolbar ? 'd-flex rc-product-sort-wrap' : 'd-none d-xl-flex'} justify-content-end`}>
                        <SortSelect
                            options={sortOptions}
                            field={search.sort.field}
                            direction={search.sort.direction}
                            onChange={onSortChange}
                            label="Sort by"
                        />
                    </div>
                )}

                {filtersShown && (
                    rcToolbar ? (
                        // RC-themed drawer (dark surface, white type, purple Apply) —
                        // same component the Listings page uses, so the two filter
                        // experiences are identical. Drop-in: same props as the legacy
                        // `SearchFilters`. `includeStoreFilter` is off because the Store
                        // filter is an admin/marketplace concern, not a shop facet.
                        <RCFilters
                            active={filtersShown}
                            search={search}
                            onApply={onFiltersApplied}
                            close={() => setFiltersShown(false)}
                            includeStoreFilter={false}
                        />
                    ) : (
                        <SearchFilters
                            active={filtersShown}
                            search={search}
                            onApply={onFiltersApplied}
                            close={() => setFiltersShown(false)}
                        />
                    )
                )}
            </div>
        </div>
    )
}

export default Search
